import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, formatApiError } from "@/contexts/AuthContext";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LogOut, Camera, Shield, CreditCard, Headphones, Send, Bell, Trash2, AlertTriangle, FileText, ChevronDown, ChevronUp, Settings as SettingsIcon } from "lucide-react";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function SupportSection() {
  const [open, setOpen] = useState(false);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    if (!subject.trim() || !message.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/support/ticket`, { subject: subject.trim(), message: message.trim() }, { withCredentials: true });
      toast.success("Consulta enviada. Te responderemos lo antes posible.");
      setSubject(""); setMessage(""); setOpen(false);
    } catch { toast.error("Error al enviar"); }
    finally { setSending(false); }
  };

  return (
    <div className="bg-[#FDFAF4] rounded-2xl border border-[#5B3E2E]/10 mb-4 overflow-hidden">
      <button data-testid="support-toggle" onClick={() => setOpen(!open)} className="w-full p-4 flex items-center gap-3 hover:bg-[#F0E7D3] transition-colors">
        <Headphones size={20} className="text-[#E48D57]" />
        <div className="text-left flex-1">
          <p className="text-sm font-medium text-[#5B3E2E]">Atención al cliente</p>
          <p className="text-[10px] text-[#5B3E2E]/40">Contacta con el equipo de Refugium</p>
        </div>
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-3 border-t border-[#5B3E2E]/8 pt-3">
          <Input data-testid="support-subject" value={subject} onChange={e => setSubject(e.target.value)} placeholder="Asunto" className="bg-transparent border-[#5B3E2E]/15 rounded-xl text-[#5B3E2E] text-sm" />
          <Textarea data-testid="support-message" value={message} onChange={e => setMessage(e.target.value)} placeholder="Escribe tu consulta..." className="bg-transparent border-[#5B3E2E]/15 rounded-xl text-[#5B3E2E] text-sm min-h-[80px]" />
          <Button data-testid="support-send" onClick={handleSend} disabled={sending || !subject.trim() || !message.trim()} className="w-full bg-[#E48D57] hover:bg-[#D07C46] text-white rounded-full text-sm flex items-center justify-center gap-2">
            <Send size={14} />{sending ? "Enviando..." : "Enviar consulta"}
          </Button>
        </div>
      )}
    </div>
  );
}

function NotificationPreferencesSection() {
  const [open, setOpen] = useState(false);
  const [prefs, setPrefs] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || prefs) return;
    axios.get(`${API}/users/me/notification-preferences`, { withCredentials: true })
      .then(r => setPrefs(r.data))
      .catch(() => setPrefs({ new_rooms: true, admin_posts: true, events: true, room_messages: true }));
  }, [open, prefs]);

  const togglePref = async (key) => {
    if (!prefs || saving) return;
    const next = { ...prefs, [key]: !prefs[key] };
    setPrefs(next);
    setSaving(true);
    try {
      await axios.put(`${API}/users/me/notification-preferences`, { [key]: next[key] }, { withCredentials: true });
    } catch {
      setPrefs(prefs); // rollback
      toast.error("No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  const items = [
    { key: "new_rooms", label: "Nuevas salas para acompañar", desc: "Cuando un acompañad@ abre una nueva sala" },
    { key: "room_messages", label: "Mensajes nuevos en mis salas", desc: "Cuando alguien escribe en tus salas" },
    { key: "admin_posts", label: "Publicaciones del equipo Refugium", desc: "Mensajes y comunicados del equipo" },
    { key: "events", label: "Eventos próximos", desc: "Recordatorios de eventos en directo" },
  ];

  return (
    <div className="bg-[#FDFAF4] rounded-2xl border border-[#5B3E2E]/10 mb-4 overflow-hidden">
      <button data-testid="notif-prefs-toggle" onClick={() => setOpen(!open)} className="w-full p-4 flex items-center gap-3 hover:bg-[#F0E7D3] transition-colors">
        <Bell size={20} className="text-[#6B9070]" />
        <div className="text-left flex-1">
          <p className="text-sm font-medium text-[#5B3E2E]">Notificaciones</p>
          <p className="text-[10px] text-[#5B3E2E]/40">Elige qué quieres recibir</p>
        </div>
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-2 border-t border-[#5B3E2E]/8 pt-3">
          {!prefs ? (
            <p className="text-xs text-[#5B3E2E]/50 text-center py-3">Cargando…</p>
          ) : items.map(item => (
            <div key={item.key} className="flex items-start gap-3 py-2">
              <div className="flex-1 min-w-0">
                <p className="text-sm text-[#5B3E2E] font-medium">{item.label}</p>
                <p className="text-[10px] text-[#5B3E2E]/45 mt-0.5">{item.desc}</p>
              </div>
              <button
                data-testid={`notif-pref-${item.key}`}
                onClick={() => togglePref(item.key)}
                disabled={saving}
                role="switch"
                aria-checked={prefs[item.key]}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ${
                  prefs[item.key] ? "bg-[#6B9070]" : "bg-[#5B3E2E]/20"
                } ${saving ? "opacity-60" : ""}`}
              >
                <span
                  className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                    prefs[item.key] ? "translate-x-[22px]" : "translate-x-0.5"
                  }`}
                />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function DeleteAccountSection({ isAdmin, onDeleted }) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    if (confirmText.trim().toUpperCase() !== "ELIMINAR") {
      toast.error('Escribe "ELIMINAR" para confirmar');
      return;
    }
    setDeleting(true);
    try {
      await axios.delete(`${API}/users/me`, { withCredentials: true });
      toast.success("Tu cuenta ha sido eliminada. Hasta pronto.");
      if (onDeleted) await onDeleted();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo eliminar la cuenta");
    } finally {
      setDeleting(false);
    }
  };

  if (isAdmin) {
    return (
      <div className="bg-[#F0A82C]/10 border border-[#F0A82C]/20 rounded-xl p-3 text-[11px] text-[#5B3E2E]/70 leading-relaxed">
        La cuenta de administrador principal no puede eliminarse desde la app. Si necesitas darla de baja, contacta con soporte.
      </div>
    );
  }

  if (!confirmOpen) {
    return (
      <button
        data-testid="delete-account-open-confirm"
        onClick={() => setConfirmOpen(true)}
        className="w-full flex items-center gap-3 py-2 text-left group hover:opacity-90 transition-opacity"
      >
        <Trash2 size={16} className="text-[#c44a3a]/70 group-hover:text-[#c44a3a] flex-shrink-0" />
        <div className="flex-1">
          <p className="text-sm font-medium text-[#c44a3a]/90 group-hover:text-[#c44a3a]">Eliminar mi cuenta</p>
          <p className="text-[10px] text-[#5B3E2E]/45">Borrado definitivo de tu perfil y todos tus datos</p>
        </div>
      </button>
    );
  }

  return (
    <div className="bg-[#c44a3a]/5 border border-[#c44a3a]/15 rounded-2xl p-4 space-y-3">
      <div className="flex items-start gap-2">
        <AlertTriangle size={14} className="text-[#c44a3a] flex-shrink-0 mt-0.5" />
        <p className="text-[11px] text-[#5B3E2E]/80 leading-relaxed">
          Esta acción es <strong className="text-[#c44a3a]">definitiva</strong> y borrará tu perfil, mensajes, salas creadas, notificaciones, lista de espera y sesiones futuras. No hay vuelta atrás.
        </p>
      </div>
      <div>
        <Label className="text-[11px] text-[#5B3E2E]/60">Escribe <strong>ELIMINAR</strong> para confirmar</Label>
        <Input
          data-testid="delete-account-confirm-input"
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          placeholder="ELIMINAR"
          className="mt-1 bg-white border-[#c44a3a]/25 rounded-xl text-[#5B3E2E] h-9"
          style={{ fontSize: "16px" }}
        />
      </div>
      <div className="flex gap-2">
        <button
          onClick={() => { setConfirmOpen(false); setConfirmText(""); }}
          className="flex-1 text-[12px] text-[#5B3E2E]/60 hover:text-[#5B3E2E] py-2 rounded-full border border-[#5B3E2E]/15 hover:bg-[#5B3E2E]/5"
        >
          Cancelar
        </button>
        <Button
          data-testid="delete-account-confirm-btn"
          onClick={handleDelete}
          disabled={deleting || confirmText.trim().toUpperCase() !== "ELIMINAR"}
          className="flex-1 bg-[#c44a3a] hover:bg-[#a83a2d] text-white rounded-full text-[12px] font-medium flex items-center justify-center gap-1.5 disabled:opacity-40 h-9"
        >
          <Trash2 size={12} />
          {deleting ? "Eliminando…" : "Eliminar"}
        </Button>
      </div>
      <p className="text-[9px] text-[#5B3E2E]/40 text-center leading-relaxed">
        Algunos backups pueden tardar hasta 90 días en sobrescribirse por completo.
      </p>
    </div>
  );
}

function SettingsSection({ isAdmin, onDeleted }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-[#FDFAF4] rounded-2xl border border-[#5B3E2E]/10 mb-4 overflow-hidden">
      <button
        data-testid="settings-toggle"
        onClick={() => setOpen(!open)}
        className="w-full p-4 flex items-center gap-3 hover:bg-[#5B3E2E]/[0.03] transition-colors"
      >
        <SettingsIcon size={18} className="text-[#5B3E2E]/70 flex-shrink-0" />
        <div className="flex-1 text-left">
          <p className="text-sm font-medium text-[#5B3E2E]">Configuración y privacidad</p>
          <p className="text-[10px] text-[#5B3E2E]/45">Datos de tu cuenta y ajustes avanzados</p>
        </div>
        {open ? <ChevronUp size={16} className="text-[#5B3E2E]/40" /> : <ChevronDown size={16} className="text-[#5B3E2E]/40" />}
      </button>
      {open && (
        <div className="px-4 pb-4 border-t border-[#5B3E2E]/8 pt-3">
          <DeleteAccountSection isAdmin={isAdmin} onDeleted={onDeleted} />
        </div>
      )}
    </div>
  );
}


export default function ProfilePage() {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState(user?.username || "");
  const [bio, setBio] = useState(user?.bio || "");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [subscription, setSubscription] = useState(null);

  useEffect(() => {
    axios.get(`${API}/users/subscription`, { withCredentials: true })
      .then(r => setSubscription(r.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    setUsername(user?.username || "");
    setBio(user?.bio || "");
  }, [user?.username, user?.bio]);

  const handleSave = async () => {
    if (!username.trim()) return;
    setSaving(true);
    try {
      await axios.put(`${API}/users/profile`, { username: username.trim(), bio: bio.trim() }, { withCredentials: true });
      await refreshUser();
      toast.success("Perfil actualizado");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      await axios.post(`${API}/users/avatar`, formData, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } });
      await refreshUser();
      toast.success("Foto actualizada");
    } catch {
      toast.error("Error al subir la foto");
    } finally {
      setUploading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const handleManageSubscription = async () => {
    try {
      const origin = window.location.origin;
      const { data } = await axios.post(`${API}/payments/portal`, { origin_url: origin }, { withCredentials: true });
      if (data.url) window.location.href = data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo abrir el portal");
    }
  };

  return (
    <div data-testid="profile-page" className="max-w-lg mx-auto px-4 py-6">
      <h1 className="font-heading text-2xl text-[#5B3E2E] mb-6">Mi Perfil</h1>

      <div className="bg-[#FDFAF4] rounded-3xl border border-[#5B3E2E]/10 p-6 mb-4">
        {/* Avatar */}
        <div className="flex flex-col items-center mb-6">
          <div className="relative">
            <div className="w-20 h-20 rounded-full bg-[#5B3E2E]/10 flex items-center justify-center overflow-hidden">
              {user?.avatar_url ? (
                <img src={`${process.env.REACT_APP_BACKEND_URL}${user.avatar_url}`} alt="" className="w-full h-full object-cover" />
              ) : (
                <span className="text-2xl font-heading text-[#5B3E2E]">{user?.username?.charAt(0)?.toUpperCase()}</span>
              )}
            </div>
            <label className="absolute bottom-0 right-0 w-7 h-7 rounded-full bg-[#E48D57] flex items-center justify-center cursor-pointer hover:bg-[#D07C46] transition-colors">
              <Camera size={14} className="text-white" />
              <input type="file" accept="image/*" className="hidden" onChange={handleAvatarUpload} data-testid="avatar-upload" />
            </label>
          </div>
          {uploading && <p className="text-xs text-[#E48D57] mt-2">Subiendo foto...</p>}
        </div>

        {/* Username */}
        <div className="space-y-4">
          <div>
            <Label className="text-[#5B3E2E] text-sm">Nombre de usuario</Label>
            <Input data-testid="profile-username" value={username} onChange={(e) => setUsername(e.target.value)} className="mt-1 bg-transparent border-[#5B3E2E]/20 rounded-xl text-[#5B3E2E]" />
          </div>
          <div>
            <Label className="text-[#5B3E2E] text-sm">Email</Label>
            <Input value={user?.email || ""} disabled className="mt-1 bg-[#5B3E2E]/5 border-[#5B3E2E]/10 rounded-xl text-[#5B3E2E]/50" />
          </div>
          <div>
            <div className="flex items-center justify-between">
              <Label className="text-[#5B3E2E] text-sm">Biografía <span className="text-[#5B3E2E]/40 text-xs font-normal">(opcional)</span></Label>
              <span className="text-[10px] text-[#5B3E2E]/40">{bio.length}/200</span>
            </div>
            <Textarea
              data-testid="profile-bio"
              value={bio}
              onChange={(e) => setBio(e.target.value.slice(0, 200))}
              placeholder="Cuéntale a la comunidad algo sobre ti..."
              className="mt-1 bg-transparent border-[#5B3E2E]/20 rounded-xl text-[#5B3E2E] text-sm min-h-[80px] resize-none"
              maxLength={200}
            />
            <p className="text-[10px] text-[#5B3E2E]/40 mt-1">Visible para los miembros de tus salas cuando pinchen en tu nombre.</p>
          </div>
          <Button data-testid="save-profile-btn" onClick={handleSave} disabled={saving || (username === user?.username && bio === (user?.bio || ""))} className="w-full bg-[#E48D57] hover:bg-[#D07C46] text-white rounded-full font-medium">
            {saving ? "Guardando..." : "Guardar cambios"}
          </Button>
        </div>
      </div>

      {/* Admin Link */}
      {(user?.role === "admin" || user?.role === "secondary_admin") && (
        <button data-testid="admin-panel-btn" onClick={() => navigate("/app/admin")} className="w-full bg-[#FDFAF4] rounded-2xl border border-[#5B3E2E]/10 p-4 flex items-center gap-3 mb-4 hover:bg-[#F0E7D3] transition-colors">
          <Shield size={20} className="text-[#6B9070]" />
          <span className="text-sm font-medium text-[#5B3E2E]">Panel de Administración</span>
        </button>
      )}

      {/* Notification preferences */}
      <NotificationPreferencesSection />

      {/* Support */}
      <SupportSection />

      {/* Legales */}
      <div className="bg-[#FDFAF4] rounded-2xl border border-[#5B3E2E]/10 p-4 mb-4">
        <p className="text-[10px] tracking-[0.2em] uppercase text-[#5B3E2E]/40 font-semibold mb-3 flex items-center gap-2">
          <FileText size={12} /> Información legal
        </p>
        <div className="flex flex-col gap-2">
          <Link to="/terms" data-testid="profile-terms-link" className="text-sm text-[#5B3E2E] hover:text-[#E48D57] underline-offset-2 hover:underline">
            Términos y Condiciones
          </Link>
          <Link to="/privacy" data-testid="profile-privacy-link" className="text-sm text-[#5B3E2E] hover:text-[#E48D57] underline-offset-2 hover:underline">
            Política de Privacidad
          </Link>
        </div>
      </div>

      {/* Configuración y privacidad (contiene "Eliminar cuenta" — patrón estándar en redes sociales) */}
      <SettingsSection isAdmin={user?.role === "admin"} onDeleted={async () => { await logout(); navigate("/"); }} />

      {/* Gestionar suscripción — botón discreto al final. Sin precios ni CTAs de compra dentro de la app. */}
      {subscription?.has_stripe_customer && (
        <button
          data-testid="manage-subscription-btn"
          onClick={handleManageSubscription}
          className="w-full flex items-center justify-center gap-2 py-3 text-[#5B3E2E]/60 hover:text-[#5B3E2E] transition-colors mb-1"
        >
          <CreditCard size={16} />
          <span className="text-sm">Gestionar suscripción</span>
        </button>
      )}

      {/* Logout */}
      <button data-testid="logout-btn" onClick={handleLogout} className="w-full flex items-center justify-center gap-2 py-3 text-[#D9534F] hover:text-[#D9534F]/80 transition-colors">
        <LogOut size={18} />
        <span className="text-sm font-medium">Cerrar sesión</span>
      </button>
    </div>
  );
}